import { Clinic } from "pwapi";

import {
  UserList,
  LabList,
  AccessionPrefixList,
  LabStationList,
  ClinicList,
  PatientList,
  CaseList,
  AccessionList,
  WorkProcessList,
  WorkProcessTaskList,
  StainList,
  ExternalLabList,
  ExternalLabStainList,
  LabStainList,
  LabPanelList,
  AntigenRetrievalMethodList,
  AntibodySpeciesList,
} from "custom-models/frontLoader";

import { CASE_WITH_WORK_ACTIONS, LIS } from "../actions/constants";
import { frontLoaderConstants, DEAUTHENTICATE } from "../actions/constants";
import { PWAction } from "../utils/redux";

// Front-Loader Reducer Creator to minimize boilerplate:

type FrontLoaderState<RecordType> = {
  [key: number]: RecordType;
};

const createFrontLoaderReducer =
  <StateType, RecordType>(
    successConstant: string,
    model: string,
    reducer: (state: StateType, action: PWAction) => StateType = (state) =>
      state
  ) =>
  (state = {} as StateType, action: PWAction): StateType => {
    // TODO: Would be nice to extract RecordType from StateType instead of having to pass both
    if (action.type === successConstant) {
      const newState = action.payload.response.reduce(
        (
          records: { [key: number]: RecordType },
          record: RecordType & { id: number }
        ) => ({ ...records, [record.id]: record }),
        {}
      );
      return { ...state, ...newState };
    }
    if (action.type === DEAUTHENTICATE) return {} as StateType;
    return reducer(state, action);
  };

const createFrontLoaderValuesReducer =
  <StateType, RecordType>(
    successConstant: string,
    model: string,
    valuesToRecordFn: (values: Array<any>) => RecordType & { id: number },
    reducer: (state: StateType, action: PWAction) => StateType = (state) =>
      state
  ) =>
  (state = {} as StateType, action: PWAction): StateType => {
    // TODO: Would be nice to extract RecordType from StateType instead of having to pass both
    if (action.type === successConstant) {
      const newState: { [key: number]: RecordType } = {};
      if (action.payload?.response?.length > 0) {
        action.payload.response.forEach((values: Array<any>) => {
          const record = valuesToRecordFn(values);
          newState[record.id] = record;
        });
      }
      return { ...state, ...newState };
    }
    if (action.type === DEAUTHENTICATE) return {} as StateType;
    return reducer(state, action);
  };

// Reducer definitions:

export type UsersState = FrontLoaderState<UserList>;
export const usersReducer = createFrontLoaderReducer<UsersState, UserList>(
  frontLoaderConstants.USERS_SUCCESS,
  "User"
);

export type LabsState = FrontLoaderState<LabList>;
export const labsReducer = createFrontLoaderReducer<LabsState, LabList>(
  frontLoaderConstants.LABS_SUCCESS,
  "Lab"
);

export type AccessionPrefixesState = FrontLoaderState<AccessionPrefixList>;
export const accessionPrefixesReducer = createFrontLoaderReducer<
  AccessionPrefixesState,
  AccessionPrefixList
>(frontLoaderConstants.ACCESSION_PREFIXES_SUCCESS, "AccessionPrefix");

export type LabStationsState = FrontLoaderState<LabStationList>;
export const labStationsReducer = createFrontLoaderReducer<
  LabStationsState,
  LabStationList
>(frontLoaderConstants.LAB_STATIONS_SUCCESS, "LabStation");

export type ClinicsState = FrontLoaderState<ClinicList>;
export const clinicsReducer = createFrontLoaderReducer<
  ClinicsState,
  ClinicList
>(frontLoaderConstants.CLINICS_SUCCESS, "Clinic");

export type LabClinicsState = number[];
export const labClinicsReducer = (
  state: LabClinicsState = [],
  action: PWAction
): LabClinicsState => {
  switch (action.type) {
    case frontLoaderConstants.LAB_CLINICS_SUCCESS:
      return action.payload?.response?.results?.length > 0
        ? action.payload.response.results.map((clinic: Clinic) => clinic.id)
        : state;
    default:
      return state;
  }
};

// high-count record reducer definitions:

export type PatientsState = FrontLoaderState<PatientList>;
const valuesToPatientList = (values: Array<any>) =>
  ({
    id: values[0],
    clinic_id: values[1],
    first_name: values[2],
    last_name: values[3],
  } as PatientList);
const jsonToPatientList = (json: any) => ({
  id: json.id,
  clinic_id: json.clinic_id,
  first_name: json.first_name,
  last_name: json.last_name,
});
export const patientsReducer = createFrontLoaderValuesReducer<
  PatientsState,
  PatientList
>(
  frontLoaderConstants.PATIENTS_SUCCESS,
  "Patient",
  valuesToPatientList,
  (state: PatientsState, action: PWAction) => {
    if (CASE_WITH_WORK_ACTIONS.includes(action.type)) {
      const json = action.payload.response.patient;
      return {
        ...state,
        [json.id]: jsonToPatientList(json),
      };
    }

    switch (action.type) {
      case LIS.ACCESSION.PRINT.SUCCESS:
        const json = action.payload.response.created_case.patient;
        return {
          ...state,
          [json.id]: jsonToPatientList(json),
        };
    }
    return state;
  }
);

export type AccessionsState = FrontLoaderState<AccessionList>;
export const accessionsReducer = createFrontLoaderValuesReducer<
  AccessionsState,
  AccessionList
>(
  frontLoaderConstants.ACCESSIONS_SUCCESS,
  "Accession",
  (values: Array<any>) =>
    ({
      id: values[0],
      status: values[1],
      author_id: values[2],
      lab_id: values[3],
      clinic_id: values[4],
      completed_at: values[5],
      patient: values[6] || {},
      specimens: values[7] || [],
    } as AccessionList),
  (state: AccessionsState, action: PWAction) => {
    switch (action.type) {
      case LIS.ACCESSION.CREATE.SUCCESS:
      case LIS.ACCESSION.GET.SUCCESS:
      case LIS.ACCESSION.UPDATE.SUCCESS:
      case LIS.ACCESSION.PRINT.SUCCESS:
        const json = action.payload.response;
        if (!json.id) return state;

        return {
          ...state,
          [json.id]: {
            ...(state[json.id] || {}),
            status: json.status,
            author_id: json.author?.id,
            lab_id: json.lab?.id,
            clinic_id: json.clinic?.id,
            patient: json.patient,
            specimens: json.specimens,
          },
        };
      case LIS.ACCESSION.DELETE.SUCCESS:
        if (!action.args.id || !state[action.args.id]) return state;
        const { [action.args.id]: _, ...remaining } = state;
        return remaining;
      default:
        return state;
    }
  }
);

export type CasesState = FrontLoaderState<CaseList>;
const valuesToCaseList = (values: Array<any>) =>
  ({
    id: values[0],
    status: values[1],
    accessioning_lab_id: values[2],
    patient_id: values[3],
    ordering_doctor_id: values[4],
    active_assignment_id: values[5],
    screen_assignment_id: values[6],
    finalize_assignment_id: values[7],
    accession: values[8],
  } as CaseList);
const jsonToCaseList = (json: any) => ({
  id: json.id,
  status: json.status,
  accessioning_lab_id: json.accessioning_lab_id,
  patient_id: json.patient.id,
  ordering_doctor_id: json.ordering_doctor_id,
  active_assignment_id: json.active_assignment_id,
  screen_assignment_id: json.screen_assignment_id,
  finalize_assignment_id: json.finalize_assignment_id,
  accession: json.accession,
});
export const casesReducer = createFrontLoaderValuesReducer<
  CasesState,
  CaseList
>(
  frontLoaderConstants.CASES_SUCCESS,
  "Case",
  valuesToCaseList,
  (state: CasesState, action: PWAction) => {
    if (CASE_WITH_WORK_ACTIONS.includes(action.type)) {
      const json = action.payload.response;
      return {
        ...state,
        [json.id]: jsonToCaseList(json),
      };
    }

    switch (action.type) {
      case LIS.ACCESSION.PRINT.SUCCESS:
        const json = action.payload.response.created_case;
        return {
          ...state,
          [json.id]: jsonToCaseList(json),
        };
    }
    return state;
  }
);

export type WorkProcessesState = FrontLoaderState<WorkProcessList>;
const valuesToWorkProcessList = (values: Array<any>) =>
  ({
    id: values[0],
    completed_at: values[1],
    case_id: values[2],
    flag_id: values[3],
    order_id: values[4],
  } as WorkProcessList);
const jsonToWorkProcessList = (json: any, caseId: number) => ({
  id: json.id,
  completed_at: json.completed_at,
  case_id: caseId,
  flag_id: json.flag_id,
  order_id: json.order?.id,
});
export const workProcessesReducer = createFrontLoaderValuesReducer<
  WorkProcessesState,
  WorkProcessList
>(
  frontLoaderConstants.WORK_PROCESSES_SUCCESS,
  "WorkProcess",
  valuesToWorkProcessList,
  (state: WorkProcessesState, action: PWAction) => {
    if (CASE_WITH_WORK_ACTIONS.includes(action.type)) {
      const jsonCase = action.payload.response;
      const newState = { ...state };
      jsonCase.work_processes.forEach((json: any) => {
        newState[json.id] = jsonToWorkProcessList(json, jsonCase.id);
      });
      return newState;
    }

    switch (action.type) {
      case LIS.ACCESSION.PRINT.SUCCESS:
        const jsonCase = action.payload.response.created_case;
        const newState = { ...state };
        jsonCase.work_processes.forEach((json: any) => {
          newState[json.id] = jsonToWorkProcessList(json, jsonCase.id);
        });
        return newState;
    }
    return state;
  }
);

export type WorkProcessTasksState = FrontLoaderState<WorkProcessTaskList>;
const valuesToWorkProcessTaskList = (values: Array<any>) =>
  ({
    id: values[0],
    work_id: values[1],
    type: values[2],
    state: values[3],
    completed_at: values[4],
  } as WorkProcessTaskList);
const jsonToWorkProcessTaskList = (json: any, workId: number) => ({
  id: json.id,
  work_id: workId,
  type: json.type,
  state: json.state,
  completed_at: json.completed_at,
});
export const workProcessTasksReducer = createFrontLoaderValuesReducer<
  WorkProcessTasksState,
  WorkProcessTaskList
>(
  frontLoaderConstants.WORK_PROCESS_TASKS_SUCCESS,
  "WorkProcessTask",
  valuesToWorkProcessTaskList,
  (state: WorkProcessTasksState, action: PWAction) => {
    if (CASE_WITH_WORK_ACTIONS.includes(action.type)) {
      const jsonCase = action.payload.response;
      const newState = { ...state };
      jsonCase.work_processes.forEach((workJson: any) => {
        workJson.tasks.forEach((json: any) => {
          newState[json.id] = jsonToWorkProcessTaskList(json, workJson.id);
        });
      });
      return newState;
    }

    switch (action.type) {
      case LIS.ACCESSION.PRINT.SUCCESS:
        const jsonCase = action.payload.response.created_case;
        const newState = { ...state };
        jsonCase.work_processes.forEach((workJson: any) => {
          workJson.tasks.forEach((json: any) => {
            newState[json.id] = jsonToWorkProcessTaskList(json, workJson.id);
          });
        });
        return newState;
    }
    return state;
  }
);

export type StainsState = FrontLoaderState<StainList>;
export const stainsReducer = createFrontLoaderValuesReducer<
  StainsState,
  StainList
>(
  frontLoaderConstants.STAINS_SUCCESS,
  "Stain",
  (values: Array<any>): StainList => ({
    id: values[0],
    category: values[1],
    name: values[2],
    abbreviation: values[3],
    is_archived: values[4],
    deliverable: values[5],
    deliverable_document_category: values[6],
  })
);

export type LabStainsState = FrontLoaderState<LabStainList>;
export const labStainsReducer = createFrontLoaderValuesReducer<
  LabStainsState,
  LabStainList
>(
  frontLoaderConstants.LAB_STAINS_SUCCESS,
  "LabStain",
  (values: Array<any>): LabStainList => ({
    id: values[0],
    lab_id: values[1],
    stain_id: values[2],
    external_lab_stain_id: values[3],
    cutoff_time: values[4],
    turnaround_hours: values[5],
    label_override: values[6],
    antigen_retrieval_method_id: values[7],
    antibody_species_id: values[8],
    routine_stain_id: values[9],
    non_routine_stain_id: values[10],
    is_archived: values[11],
  })
);

export type ExternalLabsState = FrontLoaderState<ExternalLabList>;
export const externalLabsReducer = createFrontLoaderValuesReducer<
  ExternalLabsState,
  ExternalLabList
>(
  frontLoaderConstants.EXTERNAL_LABS_SUCCESS,
  "ExternalLab",
  (values: Array<any>): ExternalLabList => ({
    id: values[0],
    name: values[1],
  })
);

export type ExternalLabStainsState = FrontLoaderState<ExternalLabStainList>;
export const externalLabStainsReducer = createFrontLoaderValuesReducer<
  ExternalLabStainsState,
  ExternalLabStainList
>(
  frontLoaderConstants.EXTERNAL_LAB_STAINS_SUCCESS,
  "ExternalLabStain",
  (values: Array<any>): ExternalLabStainList => ({
    id: values[0],
    external_lab_id: values[1],
    stain_id: values[2],
  })
);

export type LabPanelsState = FrontLoaderState<LabPanelList>;
export const labPanelsReducer = createFrontLoaderValuesReducer<
  LabPanelsState,
  LabPanelList
>(
  frontLoaderConstants.LAB_PANELS_SUCCESS,
  "LabPanel",
  (values: Array<any>): LabPanelList => ({
    id: values[0],
    lab_id: values[1],
    name: values[2],
    lab_stain_ids: values[3] || [],
  })
);

export type AntigenRetrievalMethodsState =
  FrontLoaderState<AntigenRetrievalMethodList>;
export const antigenRetrievalMethodsReducer = createFrontLoaderReducer<
  AntigenRetrievalMethodsState,
  AntigenRetrievalMethodList
>(
  frontLoaderConstants.ANTIGEN_RETRIEVAL_METHODS_SUCCESS,
  "AntigenRetrievalMethod"
);

export type AntibodySpeciesState = FrontLoaderState<AntibodySpeciesList>;
export const antibodySpeciesReducer = createFrontLoaderReducer<
  AntibodySpeciesState,
  AntibodySpeciesList
>(frontLoaderConstants.ANTIBODY_SPECIES_SUCCESS, "AntibodySpecies");
